import { Select, Theme, styled } from '@mui/joy';
import { autocompleteClasses } from '@mui/joy/Autocomplete';

const getPopupIndicatorStyle = (theme: Theme) => ({
  backgroundColor: 'transparent',
  svg: {
    fill: theme.palette.isDarkMode ? theme.palette.neutral[500] : 'inherit',
  },
});

export const getBaseSelectStyle = (theme: Theme) => ({
  '&.Mui-error': {
    '--Input-focusedHighlight': 'var(--joy-palette-danger-500)',
  },

  backgroundColor: '#FBFCFE',
  borderColor: theme.palette.isDarkMode
    ? theme.palette.neutral[250]
    : theme.palette.neutral[300],
  '&.Mui-disabled': {
    color: theme.palette.isDarkMode
      ? theme.palette.neutral[400]
      : theme.palette.neutral[500],
    borderColor: theme.palette.isDarkMode
      ? theme.palette.neutral[250]
      : theme.palette.neutral[300],
  },
  '&.MuiSelect-colorDanger, &.MuiAutocomplete-colorDanger': {
    borderColor: theme.palette.danger[300],
    color: theme.palette.danger[500],
  },
  boxShadow: theme.palette.isDarkMode ? 'none' : '{CSS box-shadow}',
  color: theme.palette.isDarkMode ? theme.palette.neutral[600] : 'inherit',
  '&:hover': {
    backgroundColor: theme.palette.isDarkMode
      ? theme.palette.neutral[300]
      : theme.palette.neutral[100],
  },
  [`& .${autocompleteClasses.popupIndicator}`]: {
    ...getPopupIndicatorStyle(theme),
    '&:hover': {
      ...getPopupIndicatorStyle(theme),
    },
  },

  [`& .${autocompleteClasses.option}`]: {
    fontSize: theme.fontSize.sm,
    color: theme.palette.isDarkMode
      ? theme.palette.neutral[900]
      : theme.palette.neutral[700],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    display: 'block',

    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover': {
      color: theme.palette.isDarkMode
        ? `${theme.palette.neutral[900]} !important`
        : theme.palette.neutral[700],
      backgroundColor: theme.palette.isDarkMode
        ? `${theme.palette.neutral[200]} !important`
        : theme.palette.neutral[100],
    },
  },
  '& > .MuiAutocomplete-popupIndicator': {
    '&.Mui-disabled': {
      svg: {
        fill: theme.palette.isDarkMode
          ? theme.palette.neutral[400]
          : theme.palette.neutral[500],
      },
    },
  },
  '& > .MuiAutocomplete-clearIndicator': {
    display: 'none',
  },
  '&: hover': {
    '& > .MuiAutocomplete-clearIndicator': {
      display: 'inline-flex',
    },
  },
});

export const getBaseSelectListboxProps = (theme: Theme) => ({
  backgroundColor: '#FBFCFE',
  borderColor: theme.palette.isDarkMode
    ? theme.palette.neutral[250]
    : theme.palette.neutral[300],
  [`& .MuiOption-root, & .MuiAutocomplete-option`]: {
    fontSize: theme.fontSize.sm,
    color: theme.palette.isDarkMode
      ? theme.palette.neutral[900]
      : theme.palette.neutral[700],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    display: 'block',

    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover': {
      color: theme.palette.isDarkMode
        ? `${theme.palette.neutral[900]} !important`
        : theme.palette.neutral[700],
      backgroundColor: theme.palette.isDarkMode
        ? `${theme.palette.neutral[200]} !important`
        : theme.palette.neutral[100],
    },
  },
});

export const getBaseOptionStyles = (theme: Theme) => ({
  '&[aria-selected="true"], &[data-selected="true"]': {
    backgroundColor: theme.palette.isDarkMode
      ? `${theme.palette.neutral[200]} !important`
      : 'transparent',
  },
  '&[aria-selected="false"], &[data-selected="false"]': {
    backgroundColor: theme.palette.isDarkMode
      ? theme.palette.neutral[50]
      : 'transparent',
  },
  '&.MuiOption-highlighted:not([aria-selected="true"])': {
    backgroundColor: 'transparent !important',
  },
});

type AnyReactComponent = React.JSXElementConstructor<any>;

export function withBaseSelectStyle<C extends AnyReactComponent>(
  Component: C,
): C;
export function withBaseSelectStyle<
  Tag extends keyof React.JSX.IntrinsicElements,
>(Component: Tag): Tag;

export function withBaseSelectStyle(Component: any) {
  return styled(Component)(({ theme }: { theme: any }) =>
    getBaseSelectStyle(theme),
  ) as any;
}

export function withBaseSelectListBoxStyle(Component: any) {
  return styled(Component)(({ theme }: { theme: any }) =>
    getBaseSelectListboxProps(theme),
  ) as any;
}

export const BaseSelect = withBaseSelectStyle(Select);
